/**
 * ConnectedAdsLens — Phase C, Stage 2.
 *
 * Per-ad detail surface for a Brief. Surfaces the actual creatives
 * linked to the brief (direct, playbook, or source_cluster) so the
 * user can ground "what's working / what's not" in real ads, not
 * just aggregate KPIs.
 *
 * Sections:
 *   - Header: bucket counts + "X connected ads" + linkage paths used
 *   - Winning bucket: ROAS >= 2x with >= $5 spend
 *   - Neutral bucket: insufficient spend OR ROAS in 0.8x-2x range
 *   - Underperforming bucket: ROAS <= 0.8x with >= $5 spend
 *
 * Each card carries a "Use as seed" CTA that (Phase 3) opens a modal
 * to spin up a new asset pack from the winning ad with a choice of
 * "Tailored Variations" or "New Angles for Scale".
 *
 * Wiring: GET /api/v1/generation/briefs/{brief_id}/connected-ads.
 */

import { useEffect, useState } from 'react';
import {
  BarChart2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Image as ImageIcon,
  Layers,
  Loader2,
  Sparkles,
  Tag,
  TrendingDown,
  TrendingUp,
  Video,
  X,
  ZoomIn,
} from 'lucide-react';
import {
  generationApi,
  type BriefConnectedAdsResponse,
  type ConnectedAd,
  type ConnectedAdBucket,
  type GenerationBrief,
} from '@/services/api';
import { SeedFromAdModal } from '@/components/brief/SeedFromAdModal';

interface Props {
  brief: GenerationBrief;
}

const BUCKET_META: Record<
  ConnectedAdBucket,
  {
    label: string;
    description: string;
    accent: string;
    badge: string;
    icon: typeof TrendingUp;
  }
> = {
  winning: {
    label: 'Winning',
    description: 'ROAS ≥ 2x with meaningful spend — these are your formulas to scale',
    accent: 'border-green-200 bg-green-50/40',
    badge: 'bg-green-100 text-green-700 border border-green-300',
    icon: TrendingUp,
  },
  neutral: {
    label: 'Neutral',
    description:
      'Either insufficient spend (<$5) or middling ROAS — needs more spend or iteration to call',
    accent: 'border-slate-200 bg-slate-50/40',
    badge: 'bg-slate-100 text-slate-700 border border-slate-300',
    icon: ImageIcon,
  },
  underperforming: {
    label: 'Underperforming',
    description: 'ROAS ≤ 0.8x with real spend — review formula or pause',
    accent: 'border-amber-200 bg-amber-50/40',
    badge: 'bg-amber-100 text-amber-700 border border-amber-300',
    icon: TrendingDown,
  },
};

const BUCKET_ORDER: ConnectedAdBucket[] = ['winning', 'neutral', 'underperforming'];

function fmtCurrency(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
}

function fmtRoas(n: number | null): string {
  if (n === null || !isFinite(n)) return '—';
  return `${n.toFixed(2)}x`;
}

function fmtCtr(n: number | null): string {
  if (n === null || !isFinite(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

function FormulaChip({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
      <span className="text-slate-400 uppercase tracking-wide">{label}</span>
      <span className="font-medium text-slate-700">{value}</span>
    </span>
  );
}

// ── Ad Quick View Modal ───────────────────────────────────────────────────────

interface AdQuickViewModalProps {
  ad: ConnectedAd;
  brief: GenerationBrief;
  bucket: ConnectedAdBucket;
  onClose: () => void;
  onUseAsSeed: (ad: ConnectedAd) => void;
}

function StatBox({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">{label}</div>
      <div className={`text-lg font-bold ${highlight ? 'text-green-700' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

function AdQuickViewModal({ ad, brief, bucket, onClose, onUseAsSeed }: AdQuickViewModalProps) {
  const isVideo = ad.media_type === 'video';
  const meta = BUCKET_META[bucket];
  const BucketIcon = meta.icon;

  const formulaFields = [
    { label: 'Hook', value: ad.hook_type },
    { label: 'Angle', value: ad.angle_archetype },
    { label: 'Emotion', value: ad.primary_emotion },
    { label: 'CTA', value: ad.cta_strategy },
    { label: 'Visual', value: ad.visual_style },
  ].filter((f) => f.value);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${meta.badge}`}>
              <BucketIcon size={11} />
              {meta.label}
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900 truncate">{ad.name || ad.creative_id}</h3>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                <span className="font-mono">{ad.creative_id}</span>
                {ad.linkage !== 'unknown' && (
                  <>
                    <span>·</span>
                    <span>linked via <span className="text-slate-500">{ad.linkage.replace('_', ' ')}</span></span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {ad.source_url && (
              <a
                href={ad.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-600 p-1"
                title="Open source"
              >
                <ExternalLink size={15} />
              </a>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Main layout: large thumbnail left, KPIs + concepts right */}
          <div className="grid grid-cols-[340px_1fr] gap-6 items-start">
            {/* Thumbnail — large reference view */}
            <div className="w-full aspect-square bg-slate-100 rounded-xl border border-slate-200 overflow-hidden flex items-center justify-center relative shadow-sm">
              {ad.thumbnail_url ? (
                <img
                  src={ad.thumbnail_url}
                  alt={ad.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : isVideo ? (
                <Video size={56} className="text-slate-400" />
              ) : (
                <ImageIcon size={56} className="text-slate-400" />
              )}
              {isVideo && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                  VIDEO
                </div>
              )}
            </div>

            {/* Right column: KPIs → concepts → formula → copy */}
            <div className="space-y-4">
              {/* KPIs */}
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <StatBox label="Spend" value={fmtCurrency(ad.spend)} />
                  <StatBox
                    label="ROAS"
                    value={fmtRoas(ad.roas)}
                    highlight={ad.roas !== null && ad.roas >= 2}
                  />
                  <StatBox label="CTR" value={fmtCtr(ad.ctr)} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <StatBox label="Impressions" value={fmtInt(ad.impressions)} />
                  <StatBox label="Clicks" value={fmtInt(ad.clicks)} />
                  <StatBox label="Conversions" value={fmtInt(ad.conversions)} />
                </div>
                {ad.revenue > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center justify-between">
                    <span className="text-xs text-green-700 font-semibold">Revenue attributed</span>
                    <span className="text-sm font-bold text-green-800">{fmtCurrency(ad.revenue)}</span>
                  </div>
                )}
              </div>

              {/* Connected concepts — right column, below KPIs */}
              {ad.concept_ids.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                    <Tag size={12} className="text-slate-400" />
                    Connected Concepts
                    <span className="ml-1 text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full border border-slate-200">
                      {ad.concept_ids.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ad.concept_ids.map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center text-[11px] px-2 py-1 rounded-md bg-brand-purple/5 text-brand-purple border border-brand-purple/20 font-medium"
                      >
                        {id.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Creative formula */}
              {formulaFields.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
                    <Layers size={12} className="text-slate-400" />
                    Creative Formula
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formulaFields.map((f) => (
                      <div
                        key={f.label}
                        className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
                      >
                        <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{f.label}</span>
                        <span className="text-xs font-medium text-slate-700">{f.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Headline + body copy */}
              {(ad.headline || ad.body_copy) && (
                <div className="space-y-2">
                  {ad.headline && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Headline</div>
                      <div className="text-sm text-slate-800 font-medium leading-snug">{ad.headline}</div>
                    </div>
                  )}
                  {ad.body_copy && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Body Copy</div>
                      <div className="text-sm text-slate-600 leading-relaxed">{ad.body_copy}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Cluster + linkage */}
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2">
              <BarChart2 size={12} className="text-slate-400" />
              Lineage
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 text-xs text-slate-600">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Linkage path</span>
                <span className="font-medium capitalize">{ad.linkage.replace(/_/g, ' ')}</span>
              </div>
              {ad.cluster_id && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Cluster</span>
                  <span className="font-mono text-slate-600 text-[10px]">{ad.cluster_id}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Brief</span>
                <span className="font-medium text-slate-600 truncate max-w-[200px]">{brief.name}</span>
              </div>
              <div className="pt-1.5 border-t border-slate-200 text-[10px] text-slate-400 italic">
                Campaign-level ad set data available once Meta / Google connectors are linked.
              </div>
            </div>
          </div>
        </div>

        {/* Footer — prominent Use as Seed CTA */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Generate a new asset pack using this ad as the creative seed.
          </p>
          <button
            onClick={() => {
              onClose();
              onUseAsSeed(ad);
            }}
            className="inline-flex items-center gap-2 bg-brand-purple text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-purple/90 transition shadow-sm"
          >
            <Sparkles size={15} />
            Use as Seed
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ad Card ───────────────────────────────────────────────────────────────────

interface AdCardProps {
  ad: ConnectedAd;
  brief: GenerationBrief;
  bucket: ConnectedAdBucket;
  onUseAsSeed: (ad: ConnectedAd) => void;
  onQuickView: (ad: ConnectedAd) => void;
}

function AdCard({ ad, brief, bucket, onUseAsSeed, onQuickView }: AdCardProps) {
  const isVideo = ad.media_type === 'video';
  const formulaCount = [
    ad.hook_type,
    ad.angle_archetype,
    ad.primary_emotion,
    ad.cta_strategy,
    ad.visual_style,
  ].filter(Boolean).length;

  return (
    <div
      className="flex gap-3 bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md hover:border-slate-300 transition cursor-pointer group"
      onClick={() => onQuickView(ad)}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-24 h-24 bg-slate-100 rounded border border-slate-200 overflow-hidden flex items-center justify-center relative">
        {ad.thumbnail_url ? (
          <img
            src={ad.thumbnail_url}
            alt={ad.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : null}
        {!ad.thumbnail_url &&
          (isVideo ? (
            <Video size={24} className="text-slate-400" />
          ) : (
            <ImageIcon size={24} className="text-slate-400" />
          ))}
        {isVideo && ad.thumbnail_url && (
          <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1 rounded">
            VIDEO
          </div>
        )}
        {/* Hover overlay hint */}
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
          <ZoomIn size={18} className="text-white" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate" title={ad.name}>
              {ad.name || ad.creative_id}
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              <span className="font-mono">{ad.creative_id}</span>
              {ad.linkage !== 'unknown' && (
                <span className="ml-2 text-slate-400">
                  · linked via{' '}
                  <span className="text-slate-500">{ad.linkage.replace('_', ' ')}</span>
                </span>
              )}
            </div>
          </div>
          {ad.source_url && (
            <a
              href={ad.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 text-slate-400 hover:text-slate-600"
              title="Open source"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>

        {/* Performance row */}
        <div className="flex items-center gap-3 mt-2 text-[11px]">
          <span className="text-slate-400">Spend</span>
          <span className="font-semibold text-slate-700">{fmtCurrency(ad.spend)}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-400">ROAS</span>
          <span className={`font-semibold ${ad.roas !== null && ad.roas >= 2 ? 'text-green-700' : 'text-slate-700'}`}>
            {fmtRoas(ad.roas)}
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-400">CTR</span>
          <span className="font-semibold text-slate-700">{fmtCtr(ad.ctr)}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-400">{fmtInt(ad.impressions)} imp</span>
        </div>

        {/* Formula chips */}
        {formulaCount > 0 ? (
          <div className="flex flex-wrap gap-1 mt-2">
            <FormulaChip label="hook" value={ad.hook_type} />
            <FormulaChip label="angle" value={ad.angle_archetype} />
            <FormulaChip label="emotion" value={ad.primary_emotion} />
            <FormulaChip label="cta" value={ad.cta_strategy} />
            <FormulaChip label="visual" value={ad.visual_style} />
          </div>
        ) : (
          <div className="mt-2 text-[10px] text-slate-400 italic">
            Creative formula attributes not yet extracted
          </div>
        )}

        {/* Concept refs */}
        {ad.concept_ids.length > 0 && (
          <div className="mt-1.5 text-[10px] text-slate-500">
            <span className="text-slate-400">{ad.concept_ids.length} canonical concepts:</span>{' '}
            <span className="text-slate-600">
              {ad.concept_ids.slice(0, 4).join(', ').replace(/_/g, ' ')}
              {ad.concept_ids.length > 4 && ` +${ad.concept_ids.length - 4}`}
            </span>
          </div>
        )}

        {/* Action row */}
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <span className="text-[10px] text-slate-400 group-hover:text-slate-500 transition flex items-center gap-1">
            <ZoomIn size={10} />
            Click to drill down
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUseAsSeed(ad);
            }}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-purple hover:bg-brand-purple/90 px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Sparkles size={12} />
            Use as Seed
          </button>
        </div>
      </div>
    </div>
  );
}

interface BucketSectionProps {
  bucket: ConnectedAdBucket;
  ads: ConnectedAd[];
  brief: GenerationBrief;
  defaultOpen: boolean;
  onUseAsSeed: (ad: ConnectedAd) => void;
  onQuickView: (ad: ConnectedAd) => void;
}

function BucketSection({
  bucket,
  ads,
  brief,
  defaultOpen,
  onUseAsSeed,
  onQuickView,
}: BucketSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const meta = BUCKET_META[bucket];
  const Icon = meta.icon;
  const totalSpend = ads.reduce((acc, a) => acc + a.spend, 0);

  if (ads.length === 0) {
    return (
      <div className={`border rounded-lg p-3 ${meta.accent} opacity-60`}>
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-slate-400" />
          <span className="text-sm font-medium text-slate-500">{meta.label}</span>
          <span className="text-[10px] text-slate-400">no ads in this bucket</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-lg ${meta.accent}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/40 transition rounded-t-lg"
      >
        {open ? (
          <ChevronDown size={14} className="text-slate-500" />
        ) : (
          <ChevronRight size={14} className="text-slate-500" />
        )}
        <Icon size={14} className="text-slate-600" />
        <span className="text-sm font-semibold text-slate-900">{meta.label}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${meta.badge}`}>
          {ads.length}
        </span>
        <span className="text-[11px] text-slate-500 ml-1">
          · {fmtCurrency(totalSpend)} spend
        </span>
        <span className="ml-auto text-[10px] text-slate-500 italic">{meta.description}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 gap-2">
          {ads.map((ad) => (
            <AdCard
              key={ad.creative_id}
              ad={ad}
              brief={brief}
              bucket={bucket}
              onUseAsSeed={onUseAsSeed}
              onQuickView={onQuickView}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ConnectedAdsLens({ brief }: Props) {
  const [data, setData] = useState<BriefConnectedAdsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seedAd, setSeedAd] = useState<ConnectedAd | null>(null);
  const [quickViewAd, setQuickViewAd] = useState<ConnectedAd | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    generationApi
      .getBriefConnectedAds(brief.brief_id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : 'Failed to load connected ads');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brief.brief_id]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading connected ads…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const ads = data.ads || [];
  const bucketed: Record<ConnectedAdBucket, ConnectedAd[]> = {
    winning: [],
    neutral: [],
    underperforming: [],
  };
  for (const a of ads) bucketed[a.performance_bucket].push(a);

  const totalAds = ads.length;
  const winningCount = data.buckets.winning.count;
  const neutralCount = data.buckets.neutral.count;
  const underCount = data.buckets.underperforming.count;

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">Ads</h2>
          <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
            {totalAds}
          </span>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          All creatives linked to this brief — direct, playbook, or source cluster.
          {totalAds > 0 && ` Per-ad spend, ROAS, and formula breakdown across ${totalAds} ad${totalAds === 1 ? '' : 's'}.`}
        </div>
        {data.linkage_paths_used && data.linkage_paths_used.length > 0 && (
          <div className="text-[10px] text-slate-400 mt-1">
            Linked via:{' '}
            {data.linkage_paths_used.map((p) => p.replace('_', ' ')).join(', ')}
          </div>
        )}
      </div>

      {/* Bucket counter strip */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-green-50 border border-green-200 rounded p-2">
          <div className="text-[10px] uppercase tracking-wide text-green-700 font-semibold">
            Winning
          </div>
          <div className="text-lg font-bold text-green-900">{winningCount}</div>
          <div className="text-[10px] text-green-700">
            {fmtCurrency(data.buckets.winning.spend)} spend
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded p-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-700 font-semibold">
            Neutral
          </div>
          <div className="text-lg font-bold text-slate-900">{neutralCount}</div>
          <div className="text-[10px] text-slate-700">
            {fmtCurrency(data.buckets.neutral.spend)} spend
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-2">
          <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
            Underperforming
          </div>
          <div className="text-lg font-bold text-amber-900">{underCount}</div>
          <div className="text-[10px] text-amber-700">
            {fmtCurrency(data.buckets.underperforming.spend)} spend
          </div>
        </div>
      </div>

      {/* Empty state */}
      {totalAds === 0 && (
        <div className="border border-slate-200 rounded-lg p-6 text-center">
          <div className="text-sm text-slate-500">No ads are linked to this brief yet.</div>
          <div className="text-[11px] text-slate-400 mt-1">
            For strategic_rebuild briefs, the source_creative_ids should populate
            after a successful run. For generated briefs, ads land here once asset
            pack runs complete.
          </div>
        </div>
      )}

      {/* Bucket sections */}
      {totalAds > 0 &&
        BUCKET_ORDER.map((bucket) => (
          <BucketSection
            key={bucket}
            bucket={bucket}
            ads={bucketed[bucket]}
            brief={brief}
            // Open winning + underperforming by default; collapse neutral
            // (typically the largest bucket) so the actionable signal
            // surfaces first.
            defaultOpen={bucket !== 'neutral'}
            onUseAsSeed={setSeedAd}
            onQuickView={setQuickViewAd}
          />
        ))}

      {/* Ad quick-view drill-down modal */}
      {quickViewAd && !seedAd && (
        <AdQuickViewModal
          ad={quickViewAd}
          brief={brief}
          bucket={quickViewAd.performance_bucket}
          onClose={() => setQuickViewAd(null)}
          onUseAsSeed={(ad) => {
            setQuickViewAd(null);
            setSeedAd(ad);
          }}
        />
      )}

      {/* Seed-from-ad modal (Phase 3) */}
      {seedAd && (
        <SeedFromAdModal
          ad={seedAd}
          brief={brief}
          onClose={() => setSeedAd(null)}
        />
      )}
    </div>
  );
}
